import { useState, useEffect } from 'react';
import { debugLog } from '../../utils/debugLog';

/**
 * Hook to detect when the browser tab is visible or hidden
 * Uses the Page Visibility API to track tab state changes
 * 
 * @returns boolean - true when tab is visible, false when hidden
 */
export const useTabVisibility = (): boolean => {
  // Initialize with current visibility state
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    // Handle SSR case where document might not be available
    if (typeof document === 'undefined') {
      return true; // Default to visible for SSR
    }
    return !document.hidden;
  });

  useEffect(() => {
    // Handle SSR case
    if (typeof document === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      setIsVisible(visible);
      
      // Debug logging for development
      debugLog(`🔍 Tab visibility changed: ${visible ? 'VISIBLE' : 'HIDDEN'}`);
    };

    // Add event listener for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

export default useTabVisibility;
